/**
 * Shop name → URL slug, as PURE functions.
 *
 * The slug is the shop's public address (`/shop/{slug}`) and must satisfy the API's own rule (migration 0015 and
 * `store.dto.ts`): lowercase letters, digits and single hyphens, 2–63 characters, starting and ending with a letter or
 * digit. `slugify` only ever produces a string of that shape (or an empty one) — never a character the API would refuse.
 *
 * Accents are folded to their base letter (« Café Éclair » → `cafe-eclair`), the few ligatures and special letters of the
 * languages the platform serves are expanded (`œ` → `oe`), Arabic-Indic digits become ASCII digits. Letters with no Latin
 * form (Arabic, Chinese…) are NOT transliterated: a guessed transliteration would put an unreadable word in the public
 * address. They are dropped, so a name that is entirely Arabic gives an EMPTY slug — the merchant then chooses one
 * (see `slugNeedsManualEntry`).
 */

export const SLUG_MAX_LENGTH = 63;

const SPECIAL_LETTERS: Readonly<Record<string, string>> = {
  œ: 'oe',
  æ: 'ae',
  ß: 'ss',
  ø: 'o',
  đ: 'd',
  ð: 'd',
  þ: 'th',
  ł: 'l',
  ı: 'i',
};

const ARABIC_INDIC_ZERO = 0x0660;
const EXTENDED_ARABIC_INDIC_ZERO = 0x06f0;

function asciiDigits(text: string): string {
  return text.replace(/[٠-٩۰-۹]/gu, (digit) => {
    const code = digit.charCodeAt(0);
    return String(
      code >= EXTENDED_ARABIC_INDIC_ZERO
        ? code - EXTENDED_ARABIC_INDIC_ZERO
        : code - ARABIC_INDIC_ZERO,
    );
  });
}

export function slugify(name: string): string {
  const folded = asciiDigits(name)
    .normalize('NFD')
    .replace(/\p{M}/gu, '') // combining accents (also Arabic harakat)
    .toLowerCase()
    .replace(/[œæßøđðþłı]/gu, (letter) => SPECIAL_LETTERS[letter] ?? letter);

  const hyphenated = folded
    .replace(/[^a-z0-9]+/gu, '-') // anything else (spaces, punctuation, symbols, other scripts) separates words
    .replace(/^-+|-+$/gu, '');

  // Cut at the API's limit, then drop a hyphen the cut may have left at the end.
  return hyphenated.slice(0, SLUG_MAX_LENGTH).replace(/-+$/u, '');
}

/** True when a non-empty name gave no usable slug (e.g. a name written only in Arabic): the merchant must type one. */
export function slugNeedsManualEntry(name: string): boolean {
  return name.trim() !== '' && slugify(name).length < 2;
}

/** The name proposed from the account's business name (`legalName`): trimmed, within the form's 200-character limit. */
export function suggestedShopName(legalName: string | null | undefined): string {
  if (legalName === null || legalName === undefined) return '';
  return legalName.trim().slice(0, 200).trim();
}
